import {
  CreditCardFieldConfig,
  DirectDebitFieldConfig,
  FormConfig,
  PaymentFieldConfig,
  WalletFieldConfig,
} from "@evergiving/common/dist/es2015/form";
import {
  PaymentSourceKind,
  PledgePaymentAuthorization,
} from "@evergiving/common/dist/es2015/pledge";

/**
 * One payment method this form can offer, paired with the config block it is
 * rendered from. Every PaymentSourceKind maps 1:1 to a block of the same name,
 * which is what lets a link name a payment source and the form find its
 * config.
 *
 * `stripePayment` is absent on purpose. It is the deprecated kind meaning
 * "some Stripe wallet", and each wallet is now its own method with its own
 * block, so there is nothing for it to render.
 */
export type OfferedPaymentSource =
  | { kind: PaymentSourceKind.CreditCard; config: CreditCardFieldConfig }
  | { kind: PaymentSourceKind.DirectDebit; config: DirectDebitFieldConfig }
  | { kind: PaymentSourceKind.ApplePay; config: WalletFieldConfig }
  | { kind: PaymentSourceKind.GooglePay; config: WalletFieldConfig };

/**
 * The methods the donor may choose from, in a fixed order so the options do
 * not move about between forms.
 *
 * A link may authorise one payment source only. The campaign config stays the
 * authority on which methods exist at all, so an authorisation narrows the
 * choice and can never add to it. An authorisation naming a method the
 * campaign does not configure therefore leaves nothing, which the caller must
 * handle.
 */
export function offeredPaymentSources(
  payment: PaymentFieldConfig,
  authorizedPaymentSource?: PaymentSourceKind
): OfferedPaymentSource[] {
  const permits = (kind: PaymentSourceKind): boolean =>
    authorizedPaymentSource === undefined || authorizedPaymentSource === kind;

  const offered: OfferedPaymentSource[] = [];

  if (payment.creditCard && permits(PaymentSourceKind.CreditCard)) {
    offered.push({
      kind: PaymentSourceKind.CreditCard,
      config: payment.creditCard,
    });
  }

  if (payment.directDebit && permits(PaymentSourceKind.DirectDebit)) {
    offered.push({
      kind: PaymentSourceKind.DirectDebit,
      config: payment.directDebit,
    });
  }

  if (payment.applePay && permits(PaymentSourceKind.ApplePay)) {
    offered.push({
      kind: PaymentSourceKind.ApplePay,
      config: payment.applePay,
    });
  }

  if (payment.googlePay && permits(PaymentSourceKind.GooglePay)) {
    offered.push({
      kind: PaymentSourceKind.GooglePay,
      config: payment.googlePay,
    });
  }

  return offered;
}

/**
 * Whether this form can offer what the link authorises.
 *
 * A link naming no payment source restricts nothing and is always satisfiable.
 * A link naming one the campaign does not configure is a link the donor cannot
 * use, so the form says it is no longer valid rather than showing a payment
 * section with nothing in it.
 *
 * Reachable in production when a campaign wallet flag is switched off after a
 * QR code was minted. A `stripePayment` link is never satisfiable, because that
 * block is no longer rendered - each wallet is its own method now.
 */
export function authorizationIsSatisfiable(
  formConfig: FormConfig,
  authorization?: PledgePaymentAuthorization
): boolean {
  const paymentSource = authorization?.paymentSource;

  if (paymentSource === undefined) {
    return true;
  }

  if (!("payment" in formConfig)) {
    return false;
  }

  return offeredPaymentSources(formConfig.payment, paymentSource).length > 0;
}
